import { useMemo } from 'react';
import { useRouter } from 'next/router';

import { useCategoryByIdQuery, Variant } from '__generated__/graphql';
import { usePdp } from 'hooks/usePdp';
import { useProductCustomization } from 'hooks/useProductCustomization';
import { useTranslate } from 'hooks/useTranslations';
import { createProductBreadcrumbLinks } from 'utils/breadcrumbRedirects';
import { Breadcrumb } from 'ui/components/Breadcrumb';
import { Skeleton } from 'ui/components/Skeleton';
import { getSizesFromSizeGroups } from 'utils/products';
import { useIsServer } from 'hooks/useIsServer';
import { useIsCrawler } from 'hooks/useIsCrawler';

export const ProductBreadcrumb = () => {
  const router = useRouter();
  const translate = useTranslate();

  const { masterProduct, referrerCategoryId } = usePdp();
  const isCrawler = useIsCrawler();
  const isServer = useIsServer();

  const { isCustomizingProduct } = useProductCustomization();

  const variableId =
    referrerCategoryId || masterProduct?.primaryCategoryId || '';

  const [categoryByIdQuery] = useCategoryByIdQuery({
    variables: {
      id: variableId,
    },
    pause: !variableId || (isServer && !isCrawler),
  });

  const categoryById = categoryByIdQuery.data?.categoryById;

  // Generate the breadcrumb path
  const path = useMemo(() => {
    const productLink = `pd/${router.query.model}/${router.query.id}`;
    const isPersonalizingProduct = router.pathname.indexOf('personalise') > 0;

    if (masterProduct && categoryById) {
      // Use the centralized function for product breadcrumbs
      return createProductBreadcrumbLinks({
        categoryById,
        productName: masterProduct.name! as string,
        productLink,
        isCustomizing: isCustomizingProduct,
        customizeUrl: router.query.model as string,
        customizeLabel: translate('customize'),
        isPersonalizing: isPersonalizingProduct,
        personalizeLabel: translate('personaliseBreadcrumb'),
      });
    } else {
      return [{ label: 'home', url: '' }];
    }
  }, [
    router.query.model,
    router.query.id,
    router.pathname,
    isCustomizingProduct,
    translate,
    categoryById,
    masterProduct,
  ]);

  const everyVariationHasSizes = masterProduct?.variations.every(variation => {
    const retypedVariation = variation as Variant;
    const sizes = getSizesFromSizeGroups(retypedVariation?.sizeGroups);
    return sizes.length > 0;
  });

  // This is because "isQueryStale" is not reliable. This is fixed in later urql but we cannot upgrade yet.
  if (!masterProduct || (!isCrawler && !everyVariationHasSizes)) {
    return (
      <div className="flex gap-4">
        <span className="sr-only">Loading breadcrumbs</span>
        {[...Array(3)].map((_, i) => (
          <Skeleton
            key={i}
            className="w-20 h-[20px]"
            delay={`${(i + 1) * 100}ms`}
          />
        ))}
      </div>
    );
  }
  return <Breadcrumb path={path} isPdpFromPlp={!!referrerCategoryId} />;
};
